import { IconButton } from "@mui/material";
import { EditIcon } from "svgs";
import Grid from "@mui/material/Grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useDispatch } from "react-redux";
import { useFlightTranslation } from "utils/customHooks/useFlightTranslation";
import {
  updateIsRetrotagPopperOpen,
  updateRetrotagSegmentRange,
  updateRetrotagSegmentBoundingRange,
} from "store/retrotagSlice";
import type { TimeRangeFinite, ProductionSegmentType } from "types/types";
import { PnlCategoriesEnum } from "types/enums";
import { CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING } from "constants/customers";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useHandleCloseModal } from "./timelineTooltipUtils";
import { useTimelineTooltipStore } from "./timelineTooltipStore";
import { useFlightConfig } from "components/DataGetter/FlightConfig/useFlightConfig";

/** Displays the selected segment tag name and a retrotag icon if applicable */
export function TagDisplay({
  tagNameDisplay,
  openRemoveRetrotagForm,
  machemetryId,
}: {
  tagNameDisplay: string;
  openRemoveRetrotagForm: () => void;
  machemetryId: string | null;
}) {
  const [{ selectedProdSegment }] = useTimelineTooltipStore();
  const { translate } = useFlightTranslation();

  const [
    {
      general: { retrotaggingEnabled },
    },
  ] = useFlightConfig();

  const events = selectedProdSegment?.events ?? [];
  const isAlreadyRetrotaggedSegment = events.some(event =>
    Boolean(event?.datasourceGroup?.type === "retroactive")
  );

  const tagLabel = isAlreadyRetrotaggedSegment
    ? translate("Retroactively_Applied_Tag")
    : translate("Applied_Tag");

  const retrotagButton = isAlreadyRetrotaggedSegment ? (
    <DeleteIconButton openRemoveRetrotagForm={openRemoveRetrotagForm} />
  ) : (
    <EditIconButton
      selectedSegment={selectedProdSegment}
      {...{ machemetryId }}
    />
  );

  return (
    <div className="textAlignCenter centeredGrid">
      <h6 className="sectionHeader">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
        >
          <Grid item>{tagLabel}</Grid>
          {retrotaggingEnabled && <Grid item>{retrotagButton}</Grid>}
        </Grid>
      </h6>
      <div className="tag noWrap">{tagNameDisplay}</div>
    </div>
  );
}

function DeleteIconButton({
  openRemoveRetrotagForm,
}: {
  openRemoveRetrotagForm: () => void;
}) {
  return (
    <IconButton aria-label="Delete" onClick={openRemoveRetrotagForm}>
      <DeleteOutlineIcon />
    </IconButton>
  );
}

function EditIconButton({
  selectedSegment,
  machemetryId,
}: {
  selectedSegment: ProductionSegmentType | null;
  machemetryId: string | null;
}) {
  const dispatch = useDispatch();
  const handleCloseModal = useHandleCloseModal();
  const ckey = useCustomerKey();
  if (!selectedSegment) {
    return null;
  }
  const shouldDisableForUnknown = Boolean(
    CUSTOMERS_WHO_USE_SUPERVISOR_CONTEXT_TAGGING[ckey]
  );
  const isUnknownSegment =
    selectedSegment.pnl.category === PnlCategoriesEnum.unknown;

  const isOpenEndedSegment = Boolean(
    !selectedSegment.range?.lt || !selectedSegment.range?.ge
  );

  const shouldDisableButton =
    isOpenEndedSegment || (isUnknownSegment && shouldDisableForUnknown);

  const handleEditButtonClick = () => {
    dispatch(updateIsRetrotagPopperOpen({ isOpen: true, machemetryId }));
    dispatch(
      updateRetrotagSegmentRange(selectedSegment.range as TimeRangeFinite)
    );
    dispatch(
      updateRetrotagSegmentBoundingRange(
        selectedSegment.range as TimeRangeFinite
      )
    );
    handleCloseModal();
  };

  return (
    <IconButton
      className="retrotagButton"
      aria-label="Edit"
      onClick={handleEditButtonClick}
      disabled={shouldDisableButton}
    >
      <EditIcon />
    </IconButton>
  );
}
